import { useState, useEffect, useRef } from "react";

const CACHE_KEY = "gh_app_config";
const DEFAULT_POLL_MINUTES = 30;
const MAINTENANCE_POLL_MINUTES = 5;
const THROTTLE_MS = 10 * 1000;

const DEFAULT_CONFIG = {
  maintenance: { active: false, message: "", contact: "" },
  pollIntervalMinutes: DEFAULT_POLL_MINUTES,
  announcement: { active: false, message: "", type: "info" },
};

function getCached() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function setCache(config) {
  localStorage.setItem(
    CACHE_KEY,
    JSON.stringify({ config, fetchedAt: Date.now() })
  );
}

export default function useAppConfig() {
  const [config, setConfig] = useState(() => {
    const cached = getCached();
    return cached?.config || DEFAULT_CONFIG;
  });
  const intervalRef = useRef(null);
  const fetchConfigRef = useRef(null);

  fetchConfigRef.current = async () => {
    const configUrl = process.env.REACT_APP_CONFIG_URL;
    if (!configUrl) return;

    const cached = getCached();
    if (cached && Date.now() - cached.fetchedAt < THROTTLE_MS) {
      setConfig(cached.config);
      return;
    }

    try {
      const res = await fetch(configUrl, { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      setConfig(data);
      setCache(data);
    } catch {
      console.error("Network error — keep using cached/default config");
    }
  };

  useEffect(() => {
    fetchConfigRef.current();
  }, []);

  useEffect(() => {
    const pollMinutes = config?.maintenance?.active
      ? MAINTENANCE_POLL_MINUTES
      : (config?.pollIntervalMinutes || DEFAULT_POLL_MINUTES);
    const pollMs = pollMinutes * 60 * 1000;

    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => fetchConfigRef.current(), pollMs);

    return () => clearInterval(intervalRef.current);
  }, [config?.pollIntervalMinutes, config?.maintenance?.active]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchConfigRef.current();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  return config;
}
